import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog } from "primereact/dialog";
import makeStyles from "@mui/styles/makeStyles";
import Button, { ButtonProps } from "@mui/material/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";

export interface ConfirmationDialogProps {
  visible: boolean;
  header: string;
  message: string | React.ReactNode;
  onAccept: () => void;
  onClose: () => void;
  closeMessage?: string;
  icon?: "success" | "warning" | "error" | "info";
  confirmButtonColor?: ButtonProps["color"];
}

const useStyles = makeStyles((theme) => ({
  icon: {
    fontSize: "2em",
  },
  success: {
    color: theme.palette.success.main,
  },
  warning: {
    color: theme.palette.warning.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  info: {
    color: theme.palette.info.main,
  },
}));

export function ConfirmationDialog(props: ConfirmationDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [icon, setIcon] = useState(faCheckCircle);

  useEffect(() => {
    switch (props.icon) {
      case "success":
        setIcon(faCheckCircle);
        break;
      case "warning":
        setIcon(faExclamationTriangle);
        break;
      case "error":
        setIcon(faExclamationTriangle);
        break;
      case "info":
        setIcon(faInfoCircle);
        break;
    }
  }, [props.icon]);

  function handleHide() {
    props.onClose();
  }

  function handleConfirmButtonClick() {
    props.onClose();
    props.onAccept();
  }

  const header = <span>{props.header}</span>;

  const footer = (
    <>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={handleConfirmButtonClick} color={props.confirmButtonColor || "primary"}>
          {t("common.confirm")}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleHide}>
          {props.closeMessage ?? t("common.close")}
        </Button>
      </Stack>
    </>
  );

  return (
    <Dialog
      header={header}
      visible={props.visible}
      onHide={handleHide}
      footer={footer}
      breakpoints={{ "1100px": "40vw", "900px": "60vw", "600px": "90vw" }}
      style={{ width: "30vw" }}
    >
      <Stack direction="row" p={1} spacing={4}>
        {props.icon && (
          <FontAwesomeIcon
            icon={icon}
            className={clsx(classes.icon, {
              [classes.success]: props.icon === "success",
              [classes.warning]: props.icon === "warning",
              [classes.error]: props.icon === "error",
              [classes.info]: props.icon === "info",
            })}
          />
        )}
        <Box sx={{ whiteSpace: "pre-line" }}>{props.message}</Box>
      </Stack>
    </Dialog>
  );
}
